/**
 * 簡易TexturePackerモジュール
 * Packingのアルゴリズムは以下参照
 * @link https://blackpawn.com/texts/lightmaps/default.html
 * @link https://tyfkda.github.io/blog/2013/10/05/texture-pakcer.html
 */
import log from 'loglevel'

/**
 * 最大テクスチャサイズを取得する
 * @returns {Number} 環境毎の最大テクスチャサイズ
 */
const getMaxSize = () => {
  const canvas = document.createElement('canvas')
  const tempGl = canvas.getContext('webgl')
  const size = tempGl.getParameter(tempGl.MAX_TEXTURE_SIZE)
  canvas.remove()
  return size
}

const MAX_TEXTURE_SIZE = getMaxSize()

/**
 * 領域クラス
 */
class Rect {
  constructor({ x, y, w, h, object }) {
    this.x = x || 0
    this.y = y || 0
    this.w = w || 0
    this.h = h || 0
    this.object = object || null
  }
}

/**
 * ノードクラス
 */
class Node {
  constructor(rect, gap) {
    this.rect = rect || null
    this.left = null // 2分木の左側ノード
    this.right = null // 2分木の右側ノード
    this.gap = Math.round(Math.abs(+gap)) || 0
  }

  /**
   * Rectを挿入する
   * @param {Rect} rect 挿入するRect
   * @returns {Boolean} 挿入できたらtrue
   */
  insert(rect) {
    if (this.rect) {
      return this._insertToThis(rect)
    } else {
      return this.left.insert(rect) || this.right.insert(rect)
    }
  }

  /**
   * Rectを挿入する
   * @param {Rect} rect 挿入するRect
   * @returns {Boolean} 挿入できたらtrue
   */
  _insertToThis(rect) {
    // サイズオーバーの場合は挿入できない
    if (rect.w > this.rect.w || rect.h > this.rect.h) {
      return false
    }

    rect.x = this.rect.x
    rect.y = this.rect.y
    const w = rect.w
    const h = rect.h
    const dw = this.rect.w - w - this.gap
    const dh = this.rect.h - h - this.gap

    // 左側ノードに狭い領域、右側ノードに広い領域を入れる
    if (dw > dh) {
      // 左側ノードは挿入rectの下領域
      this.left = new Node(
        new Rect({
          x: this.rect.x,
          y: this.rect.y + h + this.gap,
          w,
          h: dh,
        }),
        this.gap
      )
      // 右側ノードは挿入rect+左側の余り
      this.right = new Node(
        new Rect({
          x: this.rect.x + w + this.gap,
          y: this.rect.y,
          w: dw,
          h: this.rect.h,
        }),
        this.gap
      )
    } else {
      // 左側ノードは挿入rectの右領域
      this.left = new Node(
        new Rect({
          x: this.rect.x + w + this.gap,
          y: this.rect.y,
          w: dw,
          h,
        }),
        this.gap
      )
      // 右側ノードは挿入rect+右側の余り
      this.right = new Node(
        new Rect({
          x: this.rect.x,
          y: this.rect.y + h + this.gap,
          w: this.rect.w,
          h: dh,
        }),
        this.gap
      )
    }
    this.rect = null
    return true
  }
}

/**
 * 引数が収まる最小POTサイズを取得する
 * @param {Number} x 値
 * @returns {Number} POTサイズ
 */
const getPOTSize = (x) => {
  return 1 << Math.ceil(Math.log2(x))
}

class TexturePacker {
  constructor(sortKey) {
    this.sortKey = sortKey || 'width'
  }

  /**
   * パッキング処理
   * @param {Array.<Object>} images 画像({ id, atlas, mapping })
   * @param {number} gap パック画像間に設ける gap(px) 未指定時は 0
   * @return {Object} パックデータ({ atlas, mapping })
   */
  async pack(images, gap) {
    if (!images || !images.length) {
      return this._createAtlas([])
    }

    // 横幅降順ソート
    images.sort((a, b) => b.mapping.width - a.mapping.width)
    // 全ての画像が収まる最小POTサイズで初期領域を決める
    let initialSize = this._getBoundingPOTSize(images)
    // 順に埋める
    const { rects, atlasSize } = this._insertImages(initialSize, images, gap)
    // 結果に従ってAtlasとMappingを作る
    return await this._createAtlas(atlasSize, rects)
  }

  /**
   * 全ての画像が収まる最小POTサイズを取得する
   * @param {Array.<Object>} images 画像({ atlas, mapping })
   * @returns {Rect}
   */
  _getBoundingPOTSize(images) {
    const areaSum = images
      .map((img) => img.mapping.width * img.mapping.height)
      .reduce((accum, current) => accum + current, 0)
    return getPOTSize(Math.sqrt(areaSum))
  }

  /**
   * 挿入
   * @param {Number} initialSize 初期Rectサイズ
   * @param {Array.<Object>} images 画像({ id, atlas, mapping })
   * @param {Number} gap パック画像間に設ける gap(px) 未指定時は 0
   * @returns {Array.<Rect>} 挿入先記録データ
   */
  _insertImages(initialSize, images, gap) {
    let size = initialSize
    let resultRects = []
    let continueFlg = true

    do {
      log.debug('try insert: size ', size)
      const rootNode = new Node(new Rect({ w: size, h: size }), gap)

      for (let i = 0; i < images.length; ++i) {
        const image = images[i]
        const imgRect = new Rect({
          object: image,
          x: 0,
          y: 0,
          w: image.mapping.width,
          h: image.mapping.height,
        })

        if (rootNode.insert(imgRect)) {
          resultRects.push(imgRect)
        } else {
          // 収まらないので領域を拡げてやり直し
          size *= 2
          resultRects = []
          break
        }
      }

      // 全部終わったのでループ終了
      if (resultRects.length === images.length) {
        continueFlg = false
      }
      // 最大サイズを超える場合も終了
      if (size > MAX_TEXTURE_SIZE) {
        log.error('Cannot create atlas. images too many!')
        continueFlg = false
      }
    } while (continueFlg)

    return { rects: resultRects, atlasSize: size }
  }

  /**
   * Atlas画像作成
   * @param {Number} atlasSize Atlasのサイズ
   * @param {Array.<Rect>} rects rect配列
   * @returns {Object} 結果({ atlas, mapping })
   */
  async _createAtlas(atlasSize, rects) {
    if (!rects || !rects.length) {
      return { atlas: null, mapping: {} }
    }

    const canvas = document.createElement('canvas')
    canvas.width = atlasSize
    canvas.height = atlasSize
    const ctx = canvas.getContext('2d')

    const mapping = {}
    for (const rect of rects) {
      ctx.drawImage(rect.object.atlas, rect.x, rect.y)
      mapping[rect.object.id] = {
        ...rect.object.mapping,
        x: rect.x,
        y: rect.y,
      }
    }

    const atlas = await createImageBitmap(canvas)
    // this._save(canvas, mapping)
    canvas.remove()
    return { atlas, mapping }
  }

  /**
   * Atlas画像,Mapping保存(デバッグ用)
   * @param {Element} canvas atlasが描画されたキャンバス
   * @param {Object} mapping
   */
  _save(canvas, mapping) {
    const a = document.createElement('a')
    a.href = canvas.toDataURL('image/png')
    a.download = 'atlas.png'
    a.click()

    a.href =
      'data:text/json;charset=utf-8,' +
      encodeURIComponent(JSON.stringify(mapping))
    a.download = 'mapping.json'
    a.click()

    a.remove()
  }
}

export default TexturePacker
